<template>
  <div
    class="project-case-study"
    :style="{ '--case-chrome': chromeHeight + 'px' }"
  >
    <div class="case-hero case-hero-text">
      <ProjectPageHeader project-id="insurance" context="HSBC">
        <h1>Insurance Product Configuration Engine</h1>
        <p class="project-subtitle">
          Mapping insurance requirements to the app’s screens and fields
        </p>
      </ProjectPageHeader>
      <div class="case-hero-lede">
        <p class="lede">
          At <strong>HSBC</strong>, I built an Excel/VBA tool that determines
          which screens and fields a banking app needs for each insurance
          product. The team can enter a product’s specifications,
          generate its configuration and update the shared rules as products
          and requirements change.
        </p>
      </div>
    </div>
    <ProjectChapterNav
      ref="chapterNav"
      :chapters="chapters"
      :start-number="0"
      :active-chapter="activeChapter"
      :progress="chapterProgress"
      @navigate="goToChapter"
    />
    <article class="project-narrative case-narrative">
      <section id="case-chapter-0" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>00</span>Context</span>
          <h2
            class="project-chapter-first"
            id="first-the-problem"
            data-source-anchor="first-the-problem"
          >
            Configuring the app for different insurance products
          </h2>
        </header>
        <div class="case-prose-pair" data-case-reveal>
          <p>
            Across dozens of insurance products in multiple markets, the app
            needed different screens and questions. Travel insurance might
            need trip dates, motor insurance might need vehicle details, and
            an earlier answer could determine which follow-up question
            appeared. The team needed a way to express those differences and
            generate the corresponding configuration.
          </p>
          <p>
            I connected the product specifications to the design library of available screens and fields. The resulting rules let the engine translate a product’s requirements into the component configuration engineering needed. When a requirement changed, the team could update the rule and regenerate the configuration.
          </p>
        </div>
      </section>
      <section id="case-chapter-1" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>01</span>Mapping</span>
          <h2 id="the-idea" data-source-anchor="the-idea">
            Making the configuration rules reusable
          </h2>
        </header>
        <div class="case-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              The required interface depended on combinations of product
              choices and conditions. I represented each relevant combination
              as a business scenario and defined rules for the components it
              required. This gave the engine a sequence it could evaluate, first
              establishing which scenarios applied, then selecting their
              screens and fields.
            </p>
            <p>
              Many products reuse the same definitions, conditions and
              components. Maintaining separate rules for each product would
              mean revising the same logic in several places whenever a shared
              requirement changed. I organised these into linked libraries,
              where each shared definition could be maintained once. Decision
              tables record which product values satisfy a scenario and which
              UI components it requires.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content">
              <div class="lib-cards">
                <div class="lib-card">
                  <span class="lib-tag">Product specification</span>
                  <p>
                    The questions, allowed values and conditional inputs used
                    to describe a product's requirements.
                  </p>
                </div>
                <div class="lib-card">
                  <span class="lib-tag">UI components</span>
                  <p>
                    The reusable screens, fields, and interface components
                    available in the app.
                  </p>
                </div>
                <div class="lib-card">
                  <span class="lib-tag">Decision tables</span>
                  <p>
                    The conditions that map specification values to the
                    UI-component requirements for each business scenario.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="case-reading case-copy" data-case-reveal>
          <p>
            The engine follows
            <strong>product specification → business scenarios → UI-component
              requirements</strong>. The team can follow the same path
            backwards to see which scenario selected a screen or field and
            which product values made that scenario apply. The mapping gives
            them a way to inspect the generated configuration and the rules
            behind it.
          </p>
          <p>
            A new product can reuse the existing definitions with a different
            set of values. When a shared condition changes, the team can revise
            its decision table and regenerate the affected configurations.
            Keeping these rules outside the VBA code also makes that part of
            the engine accessible to the people maintaining the requirements.
          </p>
        </div>
      </section>
      <section id="case-chapter-2" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>02</span>Try it</span>
          <h2 id="try-it" data-source-anchor="try-it">
            Try the rules on a trip
          </h2>
        </header>
        <div class="case-reading case-copy" data-case-reveal>
          <p class="play-intro">
            Change a trip’s details and trace the result through the highlighted rules to its packing list. The example below shows how conditional questions and separate decision tables work together.
          </p>
        </div>
        <div class="case-wide" data-case-reveal><InsuranceRuleEngine /></div>
      </section>
      <section id="case-chapter-3" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>03</span>Automation</span>
          <h2
            id="from-the-packing-demo-to-the-production-tool"
            data-source-anchor="from-the-packing-demo-to-the-production-tool"
          >
            Automating product configuration
          </h2>
        </header>
        <div class="case-prose-pair" data-case-reveal>
          <p>
            In the production Excel/VBA tool, users entered specifications through a dynamic form. A compact notation in each field definition determined when follow-up questions appeared. The roughly 32 KB engine evaluated these inputs against the decision tables and generated the required UI-component configuration.
          </p>
          <p>
            Decision tables combined broad defaults and specific exceptions through wildcard matching and right-side overrides. I developed four versions of the tool and included a tutorial showing colleagues how to maintain these rules and their linked libraries. After my internship, the team could continue generating configurations and updating the shared logic.
          </p>
        </div>
      </section>
    </article>
  </div>
</template>
<script>
import ProjectPageHeader from "./ui/ProjectPageHeader.vue";
import projectCaseStudyMixin from "../mixins/projectCaseStudyMixin";
import ProjectChapterNav from "./ui/ProjectChapterNav.vue";
import {
  fields,
  components,
  configurators,
  visibleFields,
  evaluate,
  defaultAnswers,
  fieldBySno,
  componentById,
} from "../data/playbookDemo";
import InsuranceRuleEngine from "./view/InsuranceRuleEngine.vue";

export default {
  name: "PlaybookTab",
  components: { ProjectChapterNav, ProjectPageHeader, InsuranceRuleEngine },
  mixins: [projectCaseStudyMixin],
  data() {
    return {
      fields,
      components,
      configurators,
      answers: defaultAnswers(),
    };
  },
  computed: {
    chapters() {
      return [
        { id: "case-chapter-0", label: "Context" },
        { id: "case-chapter-1", label: "Mapping" },
        { id: "case-chapter-2", label: "Try it" },
        { id: "case-chapter-3", label: "Automation" },
      ];
    },
    visible() {
      return visibleFields(this.answers);
    },
    hasSub() {
      return this.visible.some((f) => f.parent);
    },
    ev() {
      return evaluate(this.answers);
    },
  },
  methods: {
    fname(sno) {
      const f = fieldBySno(sno);
      return f ? f.name : sno;
    },
    iname(id) {
      const c = componentById(id);
      return c ? c.name : id;
    },
    matchCount(group) {
      return group.results.filter((r) => r.match).length;
    },
    winnerName(group) {
      return group.winning >= 0 ? group.cfg.scenarios[group.winning].name : "—";
    },
  },
};
</script>
<style scoped>
.lede {
  font-size: 1.05em;
}
.play-intro,
.stage-note {
  color: #777;
}

/* three-library cards */
.lib-cards {
  display: flex;
  gap: 12px;
  margin: 1.2rem 0 0.5rem;
  flex-wrap: wrap;
}
.lib-card {
  flex: 1 1 150px;
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  padding: 12px 14px;
}
.lib-card p {
  margin: 0.3rem 0 0;
  font-size: 0.9em;
  line-height: 1.5;
}
.lib-tag {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.72rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #333;
  border-bottom: 2px solid #cfcfcf;
  padding-bottom: 2px;
}

/* playground shell */
.playground {
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  padding: 4px 18px 18px;
  margin: 1.4rem 0;
  background: rgba(0, 0, 0, 0.012);
}
.stage {
  padding: 18px 0;
  border-bottom: 1px solid #ededed;
}
.stage:last-child {
  border-bottom: none;
}
.stage-head {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.98rem;
  color: #333;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}
.stage-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #333;
  color: #fff;
  font-size: 0.78rem;
  margin-right: 9px;
  flex-shrink: 0;
}
.stage-note {
  font-size: 0.82rem;
  line-height: 1.55;
  margin-top: 12px;
}
.stage-note--top {
  margin-top: 0;
  margin-bottom: 18px;
}

/* step 1 — form */
.qform {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 400px;
}
.qrow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.qrow.is-sub {
  padding-left: 14px;
}
.qlabel {
  font-size: 0.9em;
  color: #444;
}
.sub-arrow {
  color: #aaa;
  margin-right: 5px;
}
.qselect {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.85rem;
  padding: 5px 9px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: #fff;
  color: #222;
  min-width: 168px;
  cursor: pointer;
}

/* step 2 — configurator tables */
.cfg {
  margin-bottom: 22px;
}
.cfg:last-child {
  margin-bottom: 0;
}
.cfg-head {
  display: flex;
  align-items: baseline;
  gap: 0.6em;
  margin-bottom: 7px;
  flex-wrap: wrap;
}
.cfg-name {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.86rem;
  font-weight: 700;
  color: #2b2b2b;
}
.cfg-blurb {
  font-size: 0.78rem;
  color: #999;
  font-style: italic;
}
.cfg-note {
  font-size: 0.8rem;
  color: #555;
  margin-top: 8px;
}

.table-scroll {
  overflow-x: auto;
}
table.rules {
  border-collapse: collapse;
  width: 100%;
  font-family: "Quattrocento Sans", sans-serif;
}
table.rules th,
table.rules td {
  border: 1px solid #e4e4e4;
  padding: 6px 9px;
  font-size: 0.74rem;
  text-align: center;
  white-space: nowrap;
}
.rowhead {
  text-align: left !important;
  color: #555;
  font-weight: normal;
  background: rgba(0, 0, 0, 0.015);
}
.schead {
  color: #777;
  font-weight: normal;
  transition: background 0.2s, color 0.2s;
}
.schead.matched {
  color: #333;
  background: rgba(0, 0, 0, 0.04);
}
.schead.winning {
  color: #1f1f1f;
  background: rgba(120, 90, 170, 0.16);
  font-weight: bold;
}
.group-row td {
  text-align: left !important;
  font-size: 0.64rem !important;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #aaa;
  background: rgba(0, 0, 0, 0.02);
  border-left: none;
  border-right: none;
}
tr.dim {
  opacity: 0.38;
}
.cell {
  transition: background 0.2s, color 0.2s;
  color: #888;
}
.cell.colmatch {
  background: rgba(0, 0, 0, 0.035);
}
.cell.winning {
  background: rgba(120, 90, 170, 0.12);
}
.spec-cell {
  color: #555;
}
.spec-cell.bad {
  color: #c98a8a;
  text-decoration: line-through;
}
.comp-cell.yes {
  color: #2e7d4f;
  font-weight: bold;
}
.comp-cell.no {
  color: #c08a8a;
}
.comp-cell.winning.yes {
  background: rgba(46, 125, 79, 0.16);
}
.comp-cell.winning.no {
  background: rgba(192, 138, 138, 0.14);
}

/* step 3 — phone */
.phone-wrap {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  flex-wrap: wrap;
}
.phone {
  position: relative;
  width: 210px;
  flex-shrink: 0;
  border: 2px solid #333;
  border-radius: 24px;
  padding: 15px 9px 14px;
  background: #fff;
}
.phone-notch {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 5px;
  border-radius: 3px;
  background: #333;
}
.phone-screen {
  margin-top: 8px;
}
.app-bar {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.82rem;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 9px;
  margin-bottom: 9px;
  border-bottom: 1px solid #eee;
}
.app-count {
  font-size: 0.68rem;
  color: #fff;
  background: #333;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
}
.app-body {
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-height: 168px;
}
.app-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: rgba(0, 0, 0, 0.015);
  animation: cardIn 0.34s cubic-bezier(0.2, 0.65, 0.25, 1) both;
}
.app-check {
  flex-shrink: 0;
  margin-top: 1px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #2e7d4f;
  position: relative;
}
.app-check::after {
  content: "✓";
  color: #fff;
  font-size: 10px;
  font-weight: bold;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -52%);
}
.app-card-text {
  display: flex;
  flex-direction: column;
}
.app-card-title {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.78rem;
  color: #2b2b2b;
  line-height: 1.2;
}
.app-card-hint {
  font-size: 0.66rem;
  color: #999;
  margin-top: 2px;
}
.app-empty {
  font-size: 0.74rem;
  color: #aaa;
  text-align: center;
  padding-top: 60px;
}
@keyframes cardIn {
  from {
    opacity: 0;
    transform: translateY(9px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.phone-side {
  flex: 1 1 220px;
}
.phone-caption {
  font-size: 0.85em;
  color: #666;
  margin-top: 4px;
}
.legend-group {
  margin-top: 14px;
}
.legend-title {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.66rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #aaa;
}
.comp-legend {
  list-style: none;
  padding: 0;
  margin: 4px 0 0;
}
.comp-legend li {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  padding: 2px 0;
  transition: color 0.2s;
}
.comp-legend .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 9px;
  background: #d2d2d2;
  transition: background 0.2s;
}
.comp-legend li.on {
  color: #2b2b2b;
}
.comp-legend li.on .dot {
  background: #2e7d4f;
}
.comp-legend li.off {
  color: #bbb;
}

/* ── dark theme ─────────────────────────────────────────────── */
body.dark-mode .play-intro,
body.dark-mode .stage-note {
  color: var(--site-text-secondary);
}
body.dark-mode .lib-card {
  border-color: #2c2c2c;
}
body.dark-mode .lib-tag {
  color: #cfcfcf;
  border-bottom-color: #444;
}
body.dark-mode .playground {
  border-color: #2a2a2a;
  background: rgba(255, 255, 255, 0.015);
}
body.dark-mode .stage {
  border-bottom-color: #242424;
}
body.dark-mode .stage-head,
body.dark-mode .lib-card p {
  color: #cfcfcf;
}
body.dark-mode .stage-num {
  background: #cfcfcf;
  color: #000000;
}
body.dark-mode .qlabel {
  color: #c4c4c4;
}
body.dark-mode .qselect {
  background: #1a1a1a;
  color: #ddd;
  border-color: #3a3a3a;
}
body.dark-mode .cfg-name {
  color: #e0e0e0;
}
body.dark-mode .cfg-blurb {
  color: var(--site-text-tertiary);
}
body.dark-mode .cfg-note {
  color: var(--site-text-secondary);
}
body.dark-mode table.rules th,
body.dark-mode table.rules td {
  border-color: #2a2a2a;
}
body.dark-mode .rowhead {
  color: var(--site-text-secondary);
  background: rgba(255, 255, 255, 0.02);
}
body.dark-mode .schead.matched {
  color: #ddd;
  background: rgba(255, 255, 255, 0.05);
}
body.dark-mode .schead.winning {
  color: #fff;
  background: rgba(150, 120, 200, 0.28);
}
body.dark-mode .group-row td {
  color: var(--site-text-tertiary);
  background: rgba(255, 255, 255, 0.025);
}
body.dark-mode .cell {
  color: var(--site-text-tertiary);
}
body.dark-mode .cell.colmatch {
  background: rgba(255, 255, 255, 0.04);
}
body.dark-mode .cell.winning {
  background: rgba(150, 120, 200, 0.2);
}
body.dark-mode .spec-cell {
  color: #b0b0b0;
}
body.dark-mode .comp-cell.yes {
  color: #6fcf97;
}
body.dark-mode .comp-cell.no {
  color: #c98f8f;
}
body.dark-mode .phone {
  border-color: #cfcfcf;
  background: #161616;
}
body.dark-mode .phone-notch {
  background: #cfcfcf;
}
body.dark-mode .app-bar {
  color: #ddd;
  border-bottom-color: #2a2a2a;
}
body.dark-mode .app-count {
  background: #cfcfcf;
  color: #161616;
}
body.dark-mode .app-card {
  border-color: #2e2e2e;
  background: rgba(255, 255, 255, 0.02);
}
body.dark-mode .app-card-title {
  color: #ddd;
}
body.dark-mode .phone-caption {
  color: var(--site-text-secondary);
}
body.dark-mode .legend-title {
  color: var(--site-text-tertiary);
}
body.dark-mode .comp-legend li.on {
  color: #e6e6e6;
}
body.dark-mode .comp-legend li.on .dot {
  background: #6fcf97;
}

:global(html.theme-transitioning .schead),
:global(body.theme-transitioning .schead),
:global(html.theme-transitioning .cell),
:global(body.theme-transitioning .cell),
:global(html.theme-transitioning .comp-legend li),
:global(body.theme-transitioning .comp-legend li) {
  transition: color var(--theme-duration, 220ms) ease,
    background-color var(--theme-duration, 220ms) ease;
}
:global(html.theme-transitioning .comp-legend .dot),
:global(body.theme-transitioning .comp-legend .dot) {
  transition: background-color var(--theme-duration, 220ms) ease;
}

@media (max-width: 560px) {
  .qrow {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  .phone-wrap {
    justify-content: center;
  }
}
</style>
